import { ComponentId, dom, log, utils } from "@omegagrid/core";
import { DependencyLoader, DependencyLoaderEvent, Dependency } from "./dependencyLoader";
import { DataRelay } from "./dataRelay";
import { createLoader } from "./loader";
import { ReportContext } from "@reporting/rp-types/analytics";
import { extractContextParameters, extractQueryParameters } from "./parameters";
import { Resource } from "@reporting/rp-types/ext";
import { createController, type ExtensionController } from "./controllers";
import rp from "@reporting/rp-app-host/src/global";
import { BroadcastEvent, BroadcastMessage } from "@reporting/rp-app-host/src/container/broadcast";
import type {
	MultiQueryResults, CustomParams, TargetElement, CustomParamValue,EngineOptions,
	ExtensionDefinition, ExtensionDetailEventData, ExtensionWindow, CustomAction, ExportType, ExtensionOptions
} from "../types";

const logger = () => log.getLogger('rpex');

const DEFAULT_OPTIONS: Partial<EngineOptions> = {};

export class ExtensionEvent extends Event {
	container: TargetElement;
	extension: ExtensionDefinition;
	engine: Engine;
	data?: ExtensionDetailEventData;
	constructor(type: string, args: Partial<ExtensionEvent> = {}) {
		super(`extension.${type}`);
		Object.assign(this, args);
	}
}

export class Engine extends EventTarget implements Disposable {

	private _target: TargetElement;
	get target() { return this._target }

	/** the engine owns the whole document (standalone extension page) - external dependencies load only here */
	get isSandboxed() {
		return this.target instanceof HTMLBodyElement;
	}

	get id() { return this.extension.project.id_custom_page }
	get ms() { return this.options.service }

	private _data: MultiQueryResults = new Map();
	get data() { return this._data }

	private _dataLoaded = false;
	get dataLoaded() { return this._dataLoaded }

	private _renderCount = 0;
	get isFirstRender() { return this._renderCount === 1 }

	private _isSavable: boolean;
	get isSavable() {
		if (this._isSavable !== undefined) return this._isSavable;
		this._isSavable = this.extension.queries.some(q => q.type == 'save');
		return this._isSavable;
	}

	get version() { return this.extension.project.version || '2' }

	private _adapters = new Map<number, ExtensionController>();
	getAdapter(resource: Resource): ExtensionController {
		if (this._adapters.has(resource.id_custom_resource)) {
			return this._adapters.get(resource.id_custom_resource);
		}
		this._adapters.set(resource.id_custom_resource, createController(this, resource));
		return this._adapters.get(resource.id_custom_resource);
	}

	private _mainAdapter: ExtensionController;
	getMainAdapter() {
		if (this._mainAdapter) return this._mainAdapter;
		this._mainAdapter = this.getAdapter(this.extension.resources.find(r => r.id_custom_resource === this.mainResourceId));
		return this._mainAdapter;
	}

	get adapter() { return this._mainAdapter }

	readonly dataRelay = new DataRelay(this);
	readonly resources = new Map<ComponentId, string>();

	private _dependencyLoader: DependencyLoader;
	private _parameters: CustomParams;
	private _extensionOptions: ExtensionOptions;

	get extensionOptions() { return this._extensionOptions }
	get context() { return this.options.context }
	set context(context: ReportContext) { this.options.context = context }
	get contextData() { return this.options.context?.context ?? {} }

	/** replace the report context and drop the parameters derived from the old one */
	updateContext(context: ReportContext) {
		this.options.context = context;
		this._parameters = null;
	}

	get dependencyLoader() {
		if (this._dependencyLoader) return this._dependencyLoader;
		const headElement = this.headElement;
		if (!headElement) return null;
		this._dependencyLoader = new DependencyLoader(headElement);
		return this._dependencyLoader;
	}

	private _broadcastId: number;
	subscribeBroadcast = () =>	{
		if (this._broadcastId != null) return;
		this._broadcastId = rp.broadcast.subscribe(this._onBroadcast);
	};

	unsubscribeBroadcast = () => {
		if (this._broadcastId == null) return;
		rp.broadcast.unsubscribe(this._broadcastId);
		this._broadcastId = null;
	};

	private _onBroadcast = (e: BroadcastEvent) => this.dispatchEvent(new BroadcastEvent('broadcast', e.message));

	broadcast(message: BroadcastMessage) {
		message.id = this._broadcastId;
		rp.broadcast.send(message);
	}

	constructor(readonly extension: ExtensionDefinition, readonly options: EngineOptions) {
		super();
		this.options = {...DEFAULT_OPTIONS, ...options};
		this.subscribeBroadcast();
	}

	qp<T = unknown>(name: string, defaultValue?: T): T {
		return this.options.queryParams[name] as T ?? defaultValue;
	}

	get parameters() {
		if (this._parameters) return this._parameters;
		this._parameters = {
			...extractQueryParameters(this.qp('p', {})),
			...extractContextParameters(this.contextData)
		};
		return this._parameters;
	}

	cp(name: string, defaultValue?: CustomParamValue): CustomParamValue {
		return this.parameters[name] ?? defaultValue;
	}

	getResourceId(nameOrId: ComponentId): number | undefined {
		return isNaN(Number(nameOrId)) ? this.extension.resources.find(r => r.name === nameOrId)?.id_custom_resource : Number(nameOrId);
	}

	getQueryId(nameOrId: ComponentId): number | undefined {
		if (!isNaN(Number(nameOrId))) return Number(nameOrId);
		const name = String(nameOrId).replace(/\.sql$/i, '');
		return this.extension.queries.find(q => String(q.description ?? '').replace(/\.sql$/i, '') === name)?.id_thread;
	}

	async getResource(nameOrId: ComponentId, forceText = false) {
		const id = this.getResourceId(nameOrId);
		if (id == null) {
			logger().error(`Resource not found: ${nameOrId}`);
			return null;
		}

		if (this.resources.has(id)) return this.resources.get(id);
		let resource = await this.ms.get<string>(`${this.id}/resourceContent/${id}`);
		if (forceText && utils.isObject(resource)) {
			resource = JSON.stringify(resource);
		}
		this.resources.set(id, resource ?? '');
		return resource;
	}

	setResource(nameOrId: ComponentId, content: string) {
		this.resources.set(nameOrId, content);
	}

	private _mainResourceId: number;
	get mainResourceId(): number {
		if (this._mainResourceId) return this._mainResourceId;

		let idOrName: string;
		if (this.options.idResource) {
			this._mainResourceId = this.options.idResource;
		} else if (this.qp('resource')) {
			this._mainResourceId = this.getResourceId(this.qp('resource') as string);
		} else if (this.options.path) {
			this._mainResourceId = this.getResourceId(this.options.path.replace(/^\//, ''));
		} else {
			this._mainResourceId = this.extension.project.id_custom_resource;
		}

		return this._mainResourceId
	}

	getMainResource() {
		const resourceId = this.mainResourceId;
		if (!resourceId) return null;
		return this.getResource(resourceId);
	}

	getResourceQueries(idResource: number) {
		const queryIds = new Set<number>();
		this.extension.queryDependencies.forEach(dep => {
			if (dep.id_custom_resource == idResource) queryIds.add(dep.id_thread);
		});

		return this.extension.queries.filter(q => queryIds.has(q.id_thread));
	}

	setMainResource(content: string) {
		const resourceId = this.mainResourceId;
		if (!resourceId) return;
		this.setResource(resourceId, content);
		this._mainAdapter?.onMainResourceChange(content);
	}

	async loadResourceData(resourceId?: number) {
		try {
			const data = await this.dataRelay.execResource(resourceId || this.mainResourceId, this.parameters);
			data.forEach((value, key) => this._data.set(key, value));
			return this._data;
		} catch(error) {
			throw error;
		} finally {
			this._dataLoaded = true;
		}
	}

	eval(script: string) {
		const window = this.window;
		if (window.__rpex_eval__) window.__rpex_eval__(script);
		else logger().error('Eval function not available');
	}

	get window(): ExtensionWindow {
		return window;
	}

	get containerElement() {
		if (!this.target) return null;
		if (this.target instanceof Document) {
			return this.target.body;
		} else {
			return this.target;
		}
	}

	get headElement() {
		if (!this.target) return null;
		if (this.target instanceof Document) {
			return this.target.head;
		} else if (this.target instanceof HTMLBodyElement) {
			// standalone page - the engine owns the whole document
			return this.target.ownerDocument.head;
		} else {
			return null;
		}
	}

	evaluateScripts(element: HTMLElement) {
		[...(element.getElementsByTagName("script") || [])].forEach(script => {
			if (!script.type || script.type.toLowerCase() === 'text/javascript') {
				this.eval(script.text);
			}
		});
	}

	invokeAction(id: string, data?: any) {
		this.getMainAdapter()?.onAction(id, data);
	}

	getCustomActions(): CustomAction[] {
		return this.getMainAdapter()?.getCustomActions() ?? [];
	}

	getExportTypes(): ExportType[] {
		return this.getMainAdapter()?.getExportTypes() ?? [];
	}

	async export(type: ExportType) {
		await this.getMainAdapter()?.export(type);
	}

	_loader: HTMLElement;
	showLoader = () => {
		if (this.containerElement.ownerDocument !== document) return;

		this._loader ??= createLoader(this.options.staticUrl);
		this.containerElement?.appendChild(this._loader);
	}

	hideLoader = () => this._loader?.remove();

	/**
	 * Increments with every render/run; async steps of an older render bail out once
	 * a newer one starts, so a render never touches a window it was not started for
	 * (the host re-runs the engine on every reattach of its iframe).
	 */
	private _generation = 0;

	async render(target?: TargetElement, dispatchEvents = true) {
		const generation = ++this._generation;
		const superseded = () => generation !== this._generation;

		++this._renderCount;
		target = target ?? this._target;
		if (!target) return;

		const containerElement = this.containerElement;
		if (!containerElement) return;

		dom.empty(containerElement);
		this.showLoader();

		const activeResource = await this.getMainResource();
		if (superseded()) return;

		const adapter = this.getMainAdapter();
		if (!adapter) {
			this.hideLoader();
			logger().error('No extension adapter found for main resource');
			return;
		}

		if (this.isFirstRender) {
			await this.loadDependencies(adapter, dispatchEvents);
			if (superseded()) return;
		}

		await adapter.beforeRender();
		if (dispatchEvents) {
			this.dispatchEvent(new ExtensionEvent('beforeRender', {container: target, extension: this.extension, engine: this}));
		}

		if (superseded()) return;
		await adapter.render(containerElement, activeResource, this.parameters);

		await adapter.afterRender();
		if (dispatchEvents) {
			this.dispatchEvent(new ExtensionEvent('render', {container: target, extension: this.extension, engine: this}));
			this.dispatchEvent(new ExtensionEvent('afterRender', {container: target, extension: this.extension, engine: this}));
		}

		this.hideLoader();
	}

	async refresh(mainTemplateContent?: string) {
		if (mainTemplateContent) this.setMainResource(mainTemplateContent);
		await this.render(null, false);
	}

	async loadDependencies(adapter: ExtensionController, dispatchEvents = true) {
		if (!this.isSandboxed) return; // external dependencies can be loaded only on a standalone extension page

		await adapter.beforeDependencies();
		if (dispatchEvents) {
			this.dispatchEvent(new ExtensionEvent('beforeDependencies', {container: this._target, extension: this.extension, engine: this}));
		}

		const headElement = this.headElement;
		const dependencyUrls = adapter.modifyDependencies([...(this.extension.externalResources as Dependency[])]);
		if (dependencyUrls.length > 0 && headElement) {
			logger().info('Loading external dependencies:');

			const onLoad = (e: DependencyLoaderEvent) => logger().info(`\t- Loaded: ${e.dependency.url}`);
			const onError = (e: DependencyLoaderEvent) => logger().error(`\t- Error load: ${e.dependency.url}`);

			this.dependencyLoader.addEventListener('load', onLoad);
			this.dependencyLoader.addEventListener('error', onError);

			await this.dependencyLoader.load(dependencyUrls);

			this.dependencyLoader.removeEventListener('load', onLoad);
			this.dependencyLoader.removeEventListener('error', onError);
		}

		await adapter.afterDependencies();
		if (dispatchEvents) {
			this.dispatchEvent(new ExtensionEvent('afterDependencies', {container: this._target, extension: this.extension, engine: this}));
		}
	}

	async run(target: TargetElement): Promise<void> {
		this._target = target;
		this._dependencyLoader = null;
		
		logger().info('Extension definition loaded:');
		logger().info(`\tname: ${this.extension.project.name}`);
		logger().info(`\tengine version: v${this.extension.project.version}`);

		this.dispatchEvent(new ExtensionEvent('start', {container: target, extension: this.extension, engine: this}));

		this._extensionOptions = await this.getMainAdapter().getOptions();

		// render html tamplate
		await this.render();

		this.dispatchEvent(new ExtensionEvent('load', {container: target, extension: this.extension, engine: this}));
	}

	dispose() {
		this.unsubscribeBroadcast();
	}

	[Symbol.dispose](): void|Promise<void> {
		this.dispose();
	}

}
